/**
 * Curated static URL → title/description for high-value indexable paths.
 * See AGENTS.md (SEO title & meta description) for the copy formula.
 *
 * Titles follow: {primary keywords} | {intent or value} | Jobcrawls
 * Descriptions: keyword echo + aggregation value + freshness + soft CTA (~145–160 chars).
 */

export interface UrlStaticSeoMeta {
  title: string;
  description: string;
}

/** Curated copy for the English marketing home (`/` and legacy `/en` lookup). */
const EN_MARKETING_HOME_SEO: UrlStaticSeoMeta = {
  title: "Job Search Finland and Beyond | Track Every Application | Jobcrawls",
  description:
    "Jobcrawls aggregates jobs from Finnish and international boards in one search and lets you track every application in one place. Built for serious job seekers, not recruiters.",
};

/** Path keys are pathname-only (no domain), no trailing slash; `/` and `/en` use the same English home row. */
export const STATIC_URL_SEO_META: Record<string, UrlStaticSeoMeta> = {

  // ---------------------------------------------------------------------------
  // HOMEPAGES
  // ---------------------------------------------------------------------------

  "/": EN_MARKETING_HOME_SEO,

  "/en": EN_MARKETING_HOME_SEO,

  "/fi": {
    title: "Työnhaku kaikki sivustot yhdessä | Hakemusten seuranta | Jobcrawls",
    description:
      "Jobcrawls kokoaa avoimet tyopaikat Duunitorilta, Joblysta, Tyomarkkinatorilta ja kansainvalisista lahteista yhteen hakuun. Seuraa hakemuksesi samassa palvelussa. Tehty vakavaan tyonhakuun.",
  },

  "/sv": {
    title: "Jobbsok Finland och internationellt | Folj dina ansokningar | Jobcrawls",
    description:
      "Jobcrawls samlar jobb fran finska och internationella jobbsajter i en sokning och later dig folja alla ansokningar pa ett stalle. Byggt for seriosa jobbsokare, inte rekryterare.",
  },

  // ---------------------------------------------------------------------------
  // LOCATION PAGES
  // ---------------------------------------------------------------------------

  "/en/jobs/location/Helsinki_Finland": {
    title: "Jobs in Helsinki | Live Listings from All Job Sites | Jobcrawls",
    description:
      "Find Helsinki jobs from every major Finnish job board in one search. Open positions across tech, healthcare, finance and more, updated daily. Stop switching tabs and search smarter.",
  },

  "/fi/tyopaikat/alue/Helsinki_Suomi": {
    title: "Avoimet tyopaikat Helsinki | Kaikki sivustot yhdessa haussa | Jobcrawls",
    description:
      "Duunitori, Jobly ja Tyomarkkinatori nakyvat kaikki yhdessa haussa. Loyda Helsingin avoimet tyopaikat kattavammin kuin yhdeltakaan yksittaiselta sivustolta. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Helsingfors_Finland": {
    title: "Lediga jobb Helsingfors | Alla jobbsajter, en sokning | Jobcrawls",
    description:
      "Hitta lediga jobb i Helsingfors fran alla finska jobbsajter i en enda sokning. Oppna tjanster inom teknik, vard, ekonomi och mer uppdaterade dagligen. Sok smartare med Jobcrawls.",
  },

  "/en/jobs/location/Espoo_Finland": {
    title: "Jobs in Espoo | Live Listings from All Job Sites | Jobcrawls",
    description:
      "Browse Espoo jobs aggregated from every Finnish job board in one place. Roles across tech, engineering, healthcare and more updated daily. No need to visit multiple sites separately.",
  },

  "/fi/tyopaikat/alue/Espoo_Suomi": {
    title: "Avoimet tyopaikat Espoo | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Hae Espoon avoimia tyopaikkoja Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen sivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Esbo_Finland": {
    title: "Lediga jobb Esbo | Alla jobbsajter samlat pa ett stalle | Jobcrawls",
    description:
      "Hitta lediga jobb i Esbo fran alla finska jobbsajter i en sokning. Roller inom teknik, ingenjorstjanster och vard uppdateras dagligen. Enklare jobbsokning med Jobcrawls.",
  },

  "/en/jobs/location/Tampere_Finland": {
    title: "Jobs in Tampere | All Finnish Job Boards in One Search | Jobcrawls",
    description:
      "Find Tampere jobs from Duunitori, Jobly, Tyomarkkinatori and every other Finnish job board without leaving Jobcrawls. Listings across engineering, manufacturing, tech and healthcare, updated daily.",
  },

  "/fi/tyopaikat/alue/Tampere_Suomi": {
    title: "Avoimet tyopaikat Tampere | Kaikki sivustot yhdessa haussa | Jobcrawls",
    description:
      "Loyda Tampereen avoimet tyopaikat Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen tyonhakusivusto ei kata kaikkea. Jobcrawls hakee kattavammin, paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Tammerfors_Finland": {
    title: "Lediga jobb Tammerfors | Alla jobbsajter, en sokning | Jobcrawls",
    description:
      "Sok lediga jobb i Tammerfors fran alla finska jobbsajter pa ett stalle. Ingenjor, teknik, vard och tillverkning uppdateras dagligen. Jobcrawls aggregerar allt at dig.",
  },

  "/en/jobs/location/Vantaa_Finland": {
    title: "Jobs in Vantaa | All Job Sites Aggregated in One Search | Jobcrawls",
    description:
      "Search Vantaa jobs from every Finnish job board in one place. Logistics, aviation, tech and more updated daily. Jobcrawls saves you the time of visiting Duunitori, Jobly and Indeed separately.",
  },

  "/fi/tyopaikat/alue/Vantaa_Suomi": {
    title: "Avoimet tyopaikat Vantaa | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Hae Vantaan avoimia tyopaikkoja Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen sivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Vanda_Finland": {
    title: "Lediga jobb Vanda | Alla finska jobbsajter pa ett stalle | Jobcrawls",
    description:
      "Hitta lediga jobb i Vanda aggregerade fran alla finska jobbsajter. Logistik, flyg, teknik och mer uppdateras dagligen. Sluta byta mellan sajter och sok smartare med Jobcrawls.",
  },

  "/en/jobs/location/Turku_Finland": {
    title: "Jobs in Turku | Every Finnish Job Board in One Place | Jobcrawls",
    description:
      "Find Turku jobs from all major Finnish job boards without switching tabs. Maritime, pharma, tech, healthcare and more updated daily. Jobcrawls aggregates the full Turku job market for you.",
  },

  "/fi/tyopaikat/alue/Turku_Suomi": {
    title: "Avoimet tyopaikat Turku | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Loyda Turun avoimet tyopaikat Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen tyonhakusivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Abo_Finland": {
    title: "Lediga jobb Abo | Alla jobbsajter aggregerade pa ett stalle | Jobcrawls",
    description:
      "Sok lediga jobb i Abo fran alla finska jobbsajter i en sokning. Sjofart, teknik, halsa och mer uppdateras dagligen. Jobcrawls samlar hela Abos arbetsmarknad pa ett stalle.",
  },

  "/en/jobs/location/Oulu_Finland": {
    title: "Jobs in Oulu | All Finnish Job Boards in One Search | Jobcrawls",
    description:
      "Search Oulu jobs from every major Finnish job board in one place. Tech, telecom, healthcare and engineering roles updated daily. Jobcrawls covers the full Oulu job market without the tab-switching.",
  },

  "/fi/tyopaikat/alue/Oulu_Suomi": {
    title: "Avoimet tyopaikat Oulu | Kaikki sivustot yhdessa haussa | Jobcrawls",
    description:
      "Hae Oulun avoimia tyopaikkoja Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen sivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Uleaborg_Finland": {
    title: "Lediga jobb Uleaborg | Alla jobbsajter pa ett stalle | Jobcrawls",
    description:
      "Hitta lediga jobb i Uleaborg fran alla finska jobbsajter i en sokning. Teknik, telekom, halso- och sjukvard uppdateras dagligen. Jobcrawls aggregerar hela Uleaborgs arbetsmarknad.",
  },

  "/en/jobs/location/Jyvaskyla_Finland": {
    title: "Jobs in Jyvaskyla | All Job Sites in One Search | Jobcrawls",
    description:
      "Find Jyvaskyla jobs from every Finnish job board without visiting each site separately. Education, tech, logistics and more updated daily. Jobcrawls makes your Jyvaskyla job search simpler.",
  },

  "/fi/tyopaikat/alue/Jyvaskyla_Suomi": {
    title: "Avoimet tyopaikat Jyvaskyla | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Loyda Jyvaskylan avoimet tyopaikat Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen tyonhakusivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Jyvaskyla_Finland": {
    title: "Lediga jobb Jyvaskyla | Alla finska jobbsajter samlat | Jobcrawls",
    description:
      "Sok lediga jobb i Jyvaskyla fran alla finska jobbsajter pa ett stalle. Utbildning, teknik och logistik uppdateras dagligen. Enklare jobbsokning med Jobcrawls.",
  },

  "/en/jobs/location/Lahti_Finland": {
    title: "Jobs in Lahti | Every Finnish Job Board in One Search | Jobcrawls",
    description:
      "Search Lahti jobs aggregated from all major Finnish job boards. Manufacturing, logistics, tech and more updated daily. Jobcrawls pulls together the full Lahti job market so you don't have to.",
  },

  "/fi/tyopaikat/alue/Lahti_Suomi": {
    title: "Avoimet tyopaikat Lahti | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Hae Lahden avoimia tyopaikkoja Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen sivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Lahtis_Finland": {
    title: "Lediga jobb Lahtis | Alla jobbsajter aggregerade | Jobcrawls",
    description:
      "Hitta lediga jobb i Lahtis fran alla finska jobbsajter i en sokning. Tillverkning, logistik och teknik uppdateras dagligen. Jobcrawls samlar hela Lahtis arbetsmarknad pa ett stalle.",
  },

  "/en/jobs/location/Kuopio_Finland": {
    title: "Jobs in Kuopio | All Finnish Job Sites in One Search | Jobcrawls",
    description:
      "Find Kuopio jobs from every major Finnish job board without tab-switching. Healthcare, tech, education and more updated daily. Jobcrawls aggregates the full Kuopio job market for you.",
  },

  "/fi/tyopaikat/alue/Kuopio_Suomi": {
    title: "Avoimet tyopaikat Kuopio | Kaikki sivustot yhdessa haussa | Jobcrawls",
    description:
      "Loyda Kuopion avoimet tyopaikat Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen tyonhakusivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Kuopio_Finland": {
    title: "Lediga jobb Kuopio | Alla jobbsajter pa ett stalle | Jobcrawls",
    description:
      "Sok lediga jobb i Kuopio fran alla finska jobbsajter i en sokning. Halso- och sjukvard, teknik och utbildning uppdateras dagligen. Jobcrawls aggregerar hela Kuopios arbetsmarknad.",
  },

  "/en/jobs/location/Pori_Finland": {
    title: "Jobs in Pori | All Finnish Job Boards in One Place | Jobcrawls",
    description:
      "Search Pori jobs from every Finnish job board without leaving Jobcrawls. Manufacturing, energy, tech and more updated daily. Find your next role in Pori faster with one aggregated search.",
  },

  "/fi/tyopaikat/alue/Pori_Suomi": {
    title: "Avoimet tyopaikat Pori | Kaikki tyonhakusivustot yhdessa | Jobcrawls",
    description:
      "Hae Porin avoimia tyopaikkoja Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen sivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Bjorneborg_Finland": {
    title: "Lediga jobb Bjorneborg | Alla jobbsajter samlat | Jobcrawls",
    description:
      "Hitta lediga jobb i Bjorneborg fran alla finska jobbsajter pa ett stalle. Tillverkning, energi och teknik uppdateras dagligen. Jobcrawls aggregerar hela Bjorneborgs arbetsmarknad.",
  },

  "/en/jobs/location/Kotka_Finland": {
    title: "Jobs in Kotka | Every Finnish Job Board Aggregated | Jobcrawls",
    description:
      "Find Kotka jobs from all major Finnish job boards in one search. Logistics, maritime, manufacturing and more updated daily. Jobcrawls covers the full Kotka job market without the tab-switching.",
  },

  "/fi/tyopaikat/alue/Kotka_Suomi": {
    title: "Avoimet tyopaikat Kotka | Kaikki sivustot yhdessa haussa | Jobcrawls",
    description:
      "Loyda Kotkan avoimet tyopaikat Duunitorilta, Joblysta ja Tyomarkkinatorilta yhdella haulla. Yksittainen tyonhakusivusto ei kata kaikkea. Jobcrawls hakee kattavammin. Paivitetty paivittain.",
  },

  "/sv/lediga-jobb/omrade/Kotka_Finland": {
    title: "Lediga jobb Kotka | Alla finska jobbsajter pa ett stalle | Jobcrawls",
    description:
      "Sok lediga jobb i Kotka fran alla finska jobbsajter i en sokning. Logistik, sjofart och tillverkning uppdateras dagligen. Enklare jobbsokning med Jobcrawls.",
  },

  "/en/jobs/location/Remote_-_Finland": {
    title: "Remote Jobs in Finland | All Boards, Track Your Applications | Jobcrawls",
    description:
      "Search every Finnish job board for remote roles in one place and track your applications as you go. The complete remote job picture for Finland-based serious job seekers, updated daily.",
  },

  "/fi/tyopaikat/alue/Etatyo_-_Suomi": {
    title: "Etatyopaikat Suomessa | Hae kaikki sivustot, seuraa hakemukset | Jobcrawls",
    description:
      "Hae etatyopaikat kaikilta suomalaisilta tyonhakusivustoilta yhdella haulla ja seuraa hakemuksesi samassa palvelussa. Kattavampi etatyohaku kuin yhdeltakaan yksittaiselta sivustolta.",
  },

  "/sv/lediga-jobb/omrade/Distans_-_Finland": {
    title: "Distansjobb i Finland | Alla sajter, folj dina ansokningar | Jobcrawls",
    description:
      "Sok distansjobb fran alla finska jobbsajter i en sokning och folj dina ansokningar pa samma stalle. Bredare tackning an nagon enskild jobbsajt, uppdateras dagligen.",
  },

  "/en/jobs/location/Remote_-_Europe": {
    title: "Remote Jobs in Europe | For Finland-Based Job Seekers | Jobcrawls",
    description:
      "Not limiting your search to Finland? Jobcrawls surfaces European remote roles alongside Finnish listings so you see the full picture. Track every application in one place as you go.",
  },

  "/fi/tyopaikat/alue/Etatyo_-_Eurooppa": {
    title: "Etatyopaikat Eurooppa | Suomesta haettavat, kaikki yhdessa | Jobcrawls",
    description:
      "Etko rajaa hakuasi pelkastaan Suomeen? Jobcrawls nayttaa eurooppalaiset etatyopaikat suomalaisten ilmoitusten rinnalla. Seuraa kaikki hakemuksesi samassa palvelussa.",
  },

  "/sv/lediga-jobb/omrade/Distans_-_Europa": {
    title: "Distansjobb Europa | For jobbsokare baserade i Finland | Jobcrawls",
    description:
      "Begransar du inte din sokning till Finland? Jobcrawls visar europeiska distansjobb bredvid finska annonser sa att du ser hela bilden. Folj alla ansokningar pa ett stalle.",
  },

  // ---------------------------------------------------------------------------
  // STATIC PAGES
  // ---------------------------------------------------------------------------

  "/en/locations": {
    title: "Job Locations in Finland | Browse by City | Jobcrawls",
    description:
      "Browse open jobs by city across Finland. Helsinki, Tampere, Turku, Oulu and every major Finnish city covered with live listings aggregated from all job boards, updated daily.",
  },

  "/fi/sijainnit": {
    title: "Tyopaikkojen sijainnit Suomessa | Selaa kaupungeittain | Jobcrawls",
    description:
      "Selaa avoimia tyopaikkoja kaupungeittain ympari Suomen. Helsinki, Tampere, Turku, Oulu ja kaikki suurimmat kaupungit katettu tuoreilla ilmoituksilla kaikilta sivustoilta.",
  },

  "/sv/platser": {
    title: "Jobborter i Finland | Blas efter stad | Jobcrawls",
    description:
      "Blas bland lediga jobb stad for stad over hela Finland. Helsingfors, Tammerfors, Abo, Uleaborg och alla storstaeder tackta med dagliga uppdateringar fran alla jobbsajter.",
  },

  "/en/best-companies": {
    title: "Best Companies to Work for in Finland | Ranked by Hiring Activity | Jobcrawls",
    description:
      "Discover Finland's top employers ranked by active hiring and employee reputation. Compare companies across tech, healthcare, engineering and finance. Find where you actually want to work.",
  },

  "/fi/parhaat-yritykset": {
    title: "Parhaat yritykset Suomessa | Rekrytoivat aktiivisesti nyt | Jobcrawls",
    description:
      "Tutustu Suomen arvostetuimpiin tyonantajiin rekrytointiaktiivisuuden ja tyontekijamaineensa perusteella. Vertaile yrityksia teknologiassa, terveydenhuollossa ja insinoorityossa.",
  },

  "/sv/basta-foretag": {
    title: "Basta foretagen i Finland | Rankade efter rekryteringsaktivitet | Jobcrawls",
    description:
      "Utforska Finlands toppa arbetsgivare rankade efter aktiv rekrytering och anseende. Jamfor foretag inom teknik, halso- och sjukvard, ingenjorstjanster och finans.",
  },

  "/en/for-employers": {
    title: "For Employers | Reach Finland's Most Active Job Seekers | Jobcrawls",
    description:
      "Jobcrawls connects your roles with job seekers already searching across all Finnish job boards. Learn how to get your listings in front of Finland's most active candidates.",
  },

  "/fi/tyonantajille": {
    title: "Tyonantajille | Tavoita aktiivisimmat tyonhakijat Suomessa | Jobcrawls",
    description:
      "Jobcrawls kokoaa tyonhakijat, jotka etsivat toita kaikilta suomalaisilta sivustoilta. Opi miten saat ilmoituksesi Suomen aktiivisimpien tyonhakijoiden nakyviin.",
  },

  "/sv/for-arbetsgivare": {
    title: "For arbetsgivare | Na Finlands mest aktiva jobbsokare | Jobcrawls",
    description:
      "Jobcrawls aggregerar jobbsokare som soker pa alla finska jobbsajter. Lar dig hur du far dina annonser framfor Finlands mest aktiva kandidater.",
  },

  "/en/partnerships": {
    title: "Partnerships | Distribute Your Jobs Across Finland | Jobcrawls",
    description:
      "Partner with Jobcrawls to reach job seekers across all major Finnish job boards from one integration. Distribution partnerships, data partnerships and job board syndication available.",
  },

  "/fi/kumppanuudet": {
    title: "Kumppanuudet | Jaa tyopaikkasi kaikille suomalaisille alustoille | Jobcrawls",
    description:
      "Tee yhteistyo Jobcrawlsin kanssa ja tavoita tyonhakijat kaikilta suurilta suomalaisilta tyonhakusivustoilta yhden integraation kautta. Jakeluyhteistyo, datayhteistyo ja syndikointi.",
  },

  "/sv/partnerskap": {
    title: "Partnerskap | Distribuera dina jobb till hela Finland | Jobcrawls",
    description:
      "Samarbeta med Jobcrawls for att na jobbsokare pa alla stora finska jobbsajter fran en integration. Distributionspartnerskap, datapartnerskap och jobbannonssyndikning tillgangligt.",
  },

  "/en/about": {
    title: "About Jobcrawls | Built for Serious Job Seekers | Jobcrawls",
    description:
      "Jobcrawls exists because serious job seekers need more than one job board. We aggregate Finnish and international listings into one search and let you track every application in one place.",
  },

  "/fi/tietoa-jobcrawlsista": {
    title: "Tietoa Jobcrawlsista | Rakennettu vakavaan tyonhakuun | Jobcrawls",
    description:
      "Jobcrawls on rakennettu tyonhakijoille, jotka hakevat tosissaan. Kokoamme suomalaiset ja kansainvaliset tyopaikat yhteen hakuun ja annamme sinulle tyokalut seurata kaikkia hakemuksiasi.",
  },

  "/sv/om-jobcrawls": {
    title: "Om Jobcrawls | Byggt for seriosa jobbsokare | Jobcrawls",
    description:
      "Jobcrawls ar byggt for jobbsokare som soker pa allvar. Vi aggregerar finska och internationella jobb i en sokning och later dig folja alla dina ansokningar pa ett stalle.",
  },

  "/en/privacy": {
    title: "Privacy Policy | How We Handle Your Data | Jobcrawls",
    description:
      "Read Jobcrawls' privacy policy. We explain clearly what data we collect, how it is used, and how you can control it. GDPR compliant and based in Finland.",
  },

  "/fi/tietosuoja": {
    title: "Tietosuojaseloste | Miten kasittelemme tietojasi | Jobcrawls",
    description:
      "Lue Jobcrawlsin tietosuojaseloste. Kerromme selkokielella mita tietoja keraamme, miten niita kaytetaan ja miten voit hallita niita. GDPR-yhteensopiva, Suomessa toimiva palvelu.",
  },

  "/sv/integritet": {
    title: "Integritetspolicy | Hur vi hanterar dina uppgifter | Jobcrawls",
    description:
      "Las Jobcrawls integritetspolicy. Vi forklarar tydligt vilka uppgifter vi samlar in, hur de anvands och hur du kan kontrollera dem. GDPR-kompatibel tjanst baserad i Finland.",
  },

  "/en/terms": {
    title: "Terms of Service | Using Jobcrawls | Jobcrawls",
    description:
      "Read the terms governing your use of Jobcrawls. Covers job seeker use, employer access, data usage and our aggregation of Finnish job board listings.",
  },

  "/fi/kayttoehdot": {
    title: "Kayttoehdot | Jobcrawlsin kayttaminen | Jobcrawls",
    description:
      "Lue Jobcrawlsin kayttoehdot. Kattaa tyonhakijoiden kayton, tyonantajien paasyn, tietojen kayttoehtojen ja suomalaisten tyonhakusivustojen ilmoitusten yhdistamisen.",
  },

  "/sv/villkor": {
    title: "Anvandningsvillkor | Att anvanda Jobcrawls | Jobcrawls",
    description:
      "Las villkoren for din anvandning av Jobcrawls. Tackar jobbsokares anvandning, arbetsgivaratkomst, dataanvandning och var aggregering av finska jobbsajtsannonser.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCES HUB
  // ---------------------------------------------------------------------------

  "/en/resources": {
    title: "Job Search Guides for Finland | For Serious Job Seekers | Jobcrawls",
    description:
      "Salary benchmarks, job market data, CV guides and application strategy for people running a serious job search in Finland. Written for seekers who want the full picture, not just a list of jobs.",
  },

  "/fi/tyoelama": {
    title: "Tyonhakuoppaat Suomessa | Vakavaan tyonhakuun | Jobcrawls",
    description:
      "Palkkatietoja, tyomarkkinakatsauksia, CV-vinkkeja ja hakustrategiaa tyonhakijoille, jotka hakevat tosissaan. Kirjoitettu heille, jotka haluavat kokonaiskuvan pelkan ilmoituslistan sijaan.",
  },

  "/sv/arbetsliv": {
    title: "Jobbsokarguider for Finland | For seriosa jobbsokare | Jobcrawls",
    description:
      "Lonedata, arbetsmarknadsanalyser, CV-tips och sokarstrategi for dig som soker jobb pa allvar i Finland. Skrivet for dem som vill ha hela bilden, inte bara en lista med jobb.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCE ARTICLES
  // ---------------------------------------------------------------------------

  "/en/resources/remote-work-in-finland": {
    title: "Remote Work in Finland | What Job Seekers Need to Know | Jobcrawls",
    description:
      "Everything you need to know about remote work in Finland: which sectors hire remotely, what Finnish employers expect, and where to find the best remote roles across all job boards.",
  },

  "/fi/tyoelama/etatyo-suomessa": {
    title: "Etatyo Suomessa | Mita tyonhakijan tulee tietaa | Jobcrawls",
    description:
      "Kaikki oleellinen etatyosta Suomessa: milla aloilla rekrytoidaan etana, mita suomalaiset tyonantajat odottavat ja mista loydat parhaat etatyopaikat kaikilta sivustoilta.",
  },

  "/sv/arbetsliv/distansarbete-i-finland": {
    title: "Distansarbete i Finland | Vad jobbsokare behover veta | Jobcrawls",
    description:
      "Allt du behover veta om distansarbete i Finland: vilka sektorer rekryterar pa distans, vad finska arbetsgivare forvanter och var du hittar de basta distansjobben pa alla sajter.",
  },

  "/en/resources/salary-transparency-in-finnish-job-listings": {
    title: "Salary Transparency in Finnish Job Listings | What to Expect | Jobcrawls",
    description:
      "How common is salary information in Finnish job ads and what does the EU Pay Transparency Directive mean for job seekers in Finland? We analysed listings across all major job boards.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-suomalaisissa-tyopaikkailmoituksissa": {
    title: "Palkkalapinakyvyys suomalaisissa tyopaikkailmoituksissa | Jobcrawls",
    description:
      "Miten yleista palkkatiedon ilmoittaminen on suomalaisissa tyopaikkailmoituksissa ja mita EU:n palkkalapinakyvyysdirektiivi tarkoittaa tyonhakijalle? Analysoimme ilmoitukset kaikilta sivustoilta.",
  },

  "/sv/arbetsliv/lonetransparens-i-finska-jobbannonser": {
    title: "Lonetransparens i finska jobbannonser | Vad du kan forvanta dig | Jobcrawls",
    description:
      "Hur vanligt ar det med loneinformation i finska jobbannonser och vad innebar EU:s lonetransparensdirektiv for jobbsokare i Finland? Vi analyserade annonser fran alla jobbsajter.",
  },

  "/en/resources/finland-salary-transparency-law-2026": {
    title: "Finland Salary Transparency Law 2026 | What Changes for Job Seekers | Jobcrawls",
    description:
      "The EU Pay Transparency Directive takes effect in 2026. Here is what it means for job seekers in Finland, which employers are affected, and how salary disclosure will change across Finnish job boards.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026": {
    title: "Palkkalapinakyvyyslaki Suomi 2026 | Mita muuttuu tyonhakijalle | Jobcrawls",
    description:
      "EU:n palkkalapinakyvyysdirektiivi astuu voimaan 2026. Mita se tarkoittaa tyonhakijalle Suomessa, keitya tyonantajia se koskee ja miten palkkatietojen ilmoittaminen muuttuu.",
  },

  "/sv/arbetsliv/lonetransparens-lag-finland-2026": {
    title: "Lonetransparenslagen Finland 2026 | Vad andras for jobbsokare | Jobcrawls",
    description:
      "EU:s lonetransparensdirektiv trar i kraft 2026. Har ar vad det innebar for jobbsokare i Finland, vilka arbetsgivare berors och hur loneredovisningen forandras pa finska jobbsajter.",
  },

  "/en/resources/how-to-use-linkedin-to-find-a-job-in-finland": {
    title: "LinkedIn Job Search in Finland | How to Use It Effectively | Jobcrawls",
    description:
      "LinkedIn covers only a fraction of open roles in Finland. Here is how to use it strategically alongside Jobcrawls to search the full Finnish job market and not miss roles posted only on local boards.",
  },

  "/fi/tyoelama/linkedin-tyonhaku-suomessa": {
    title: "LinkedIn tyonhaku Suomessa | Miten kayttaa tehokkaasti | Jobcrawls",
    description:
      "LinkedIn kattaa vain osan Suomen avoimista tyopaikoista. Nain kaytit sita strategisesti yhdessa Jobcrawlsin kanssa, jotta etsit koko Suomen tyomarkkinat ettet jaa paitsi paikallisista ilmoituksista.",
  },

  "/sv/arbetsliv/linkedin-jobsok-i-finland": {
    title: "LinkedIn-jobbsok i Finland | Hur du anvander det effektivt | Jobcrawls",
    description:
      "LinkedIn tackar bara en del av de oppna jobben i Finland. Har ar hur du anvander det strategiskt tillsammans med Jobcrawls for att soka hela den finska arbetsmarknaden.",
  },

  "/en/resources/how-to-write-a-cv-for-a-finnish-employer": {
    title: "CV for a Finnish Employer | Format, Length and What to Include | Jobcrawls",
    description:
      "Finnish employers expect a clean, concise CV in a specific format. We cover length, structure, what to leave out and how to tailor your application for jobs found across Finnish job boards.",
  },

  "/fi/tyoelama/ansioluettelo-suomalaiselle-tyonantajalle": {
    title: "Ansioluettelo suomalaiselle tyonantajalle | Rakenne ja vinkit | Jobcrawls",
    description:
      "Suomalaiset tyonantajat odottavat selkeaa, tiivista ansioluetteloa tietyssaformaatissa. Kayns lapi pituuden, rakenteen, mitajata pois ja miten raataloida hakemuksesi.",
  },

  "/sv/arbetsliv/cv-for-finsk-arbetsgivare": {
    title: "CV for finsk arbetsgivare | Format, langd och innehall | Jobcrawls",
    description:
      "Finska arbetsgivare forvanter ett rent, kortfattat CV i ett specifikt format. Vi gaar igenom langd, struktur, vad du ska utelata och hur du skraddarsy ditt CV for finska arbetsgivare.",
  },

  "/en/resources/finland-job-market-march-2026": {
    title: "Finland Job Market March 2026 | Hiring Trends and Open Roles | Jobcrawls",
    description:
      "A data-driven look at Finland's job market in March 2026. Which sectors are growing, where hiring is slowing, and what candidates across all Finnish job boards are actually competing for.",
  },

  "/fi/tyoelama/suomen-tyomarkkinat-maaliskuu-2026": {
    title: "Suomen tyomarkkinat maaliskuu 2026 | Rekrytointitrendit | Jobcrawls",
    description:
      "Datapohjainen katsaus Suomen tyomarkkinoihin maaliskuussa 2026. Milla aloilla kasvu jatkuu, missa rekrytointi hidastuu ja mista tyopaikoista kilpaillaan kaikilla suomalaisilla tyonhakusivustoilla.",
  },

  "/sv/arbetsliv/finlands-arbetsmarknad-mars-2026": {
    title: "Finlands arbetsmarknad mars 2026 | Rekryteringstrender | Jobcrawls",
    description:
      "En datadriven bild av Finlands arbetsmarknad i mars 2026. Vilka sektorer vaxer, var avtar rekryteringen och vad konkurrerar kandidater faktiskt om pa finska jobbsajter.",
  },

  "/en/resources/helsinki-job-market-2026": {
    title: "Helsinki Job Market 2026 | Sectors Hiring and Salary Benchmarks | Jobcrawls",
    description:
      "A detailed look at Helsinki's job market in 2026. Top sectors hiring, salary benchmarks for key roles and live open positions across all Finnish job boards, updated regularly.",
  },

  "/fi/tyoelama/helsingin-tyomarkkinat-2026": {
    title: "Helsingin tyomarkkinat 2026 | Rekrytoivat alat ja palkat | Jobcrawls",
    description:
      "Yksityiskohtainen katsaus Helsingin tyomarkkinoihin vuonna 2026. Rekrytoivat toimialat, palkkavertailut keskeisille rooleille ja avoimet tyopaikat kaikilta suomalaisilta sivustoilta.",
  },

  "/sv/arbetsliv/helsingfors-arbetsmarknad-2026": {
    title: "Helsingfors arbetsmarknad 2026 | Rekryterande sektorer och loner | Jobcrawls",
    description:
      "En detaljerad bild av Helsingfors arbetsmarknad 2026. Topprekryterande sektorer, lonebenchmarks for viktiga roller och oppna tjanster fran alla finska jobbsajter.",
  },

  "/en/resources/tampere-job-market-2026": {
    title: "Tampere Job Market 2026 | Engineering, Tech and What's Hiring | Jobcrawls",
    description:
      "A data-driven look at Tampere's job market in 2026. Engineering, manufacturing, tech and healthcare roles in demand, with salary benchmarks and live listings across all Finnish job boards.",
  },

  "/fi/tyoelama/tampereen-tyomarkkinat-2026": {
    title: "Tampereen tyomarkkinat 2026 | Insinoorityot, teknologia ja trendit | Jobcrawls",
    description:
      "Datapohjainen katsaus Tampereen tyomarkkinoihin vuonna 2026. Insinoorityot, valmistus, teknologia ja terveys kysynnan kohteena. Palkkavertailut ja avoimet paikat kaikilta sivustoilta.",
  },

  "/sv/arbetsliv/tammerfors-arbetsmarknad-2026": {
    title: "Tammerfors arbetsmarknad 2026 | Ingenjor, teknik och trender | Jobcrawls",
    description:
      "En datadriven bild av Tammerfors arbetsmarknad 2026. Ingenjor, tillverkning, teknik och vard i efterfragan. Lonebenchmarks och oppna tjanster aggregerade fran alla finska jobbsajter.",
  },

  "/en/resources/technology-salary-guide-finland-2026-03": {
    title: "Tech Salary Guide Finland March 2026 | Real Pay Data | Jobcrawls",
    description:
      "Salary benchmarks for software engineers, data scientists, DevOps and other tech roles in Finland, based on live job listing data across all major Finnish job boards as of March 2026.",
  },

  "/fi/tyoelama/teknologia-alan-palkkaopas-suomi-maaliskuu-2026": {
    title: "Teknologia-alan palkkaopas Suomi maaliskuu 2026 | Oikeat palkat | Jobcrawls",
    description:
      "Palkkavertailut ohjelmistokehittajille, data-analyytikoille, DevOps-insinooroille ja muille teknologia-alan rooleille Suomessa. Perustuu kaikkien suomalaisten tyonhakusivustojen dataan maaliskuussa 2026.",
  },

  "/sv/arbetsliv/teknikloner-finland-mars-2026": {
    title: "Teknikloner Finland mars 2026 | Verklig lonedata | Jobcrawls",
    description:
      "Lonebenchmarks for mjukvaruutvecklare, datavetare, DevOps och andra tekniska roller i Finland, baserade pa live-jobbannonser fran alla stora finska jobbsajter, mars 2026.",
  },

  "/en/resources/jobs-in-finland/technology-salary-guide-finland-2026-04": {
    title: "Tech Salary Guide Finland April 2026 | Updated Pay Benchmarks | Jobcrawls",
    description:
      "Updated salary benchmarks for tech roles in Finland as of April 2026. Software, data, cloud and security roles covered using live data across all major Finnish job boards.",
  },

  "/fi/tyoelama/tyot-suomessa/teknologia-palkkaopas-suomi-2026-04": {
    title: "Teknologia-alan palkkaopas Suomi huhtikuu 2026 | Paivitetyt palkat | Jobcrawls",
    description:
      "Paivitetyt palkkavertailut teknologia-alan rooleille Suomessa huhtikuussa 2026. Ohjelmistokehitys, data, pilvi ja tietoturva katettu reaaliaikaisella datalla kaikilta suomalaisilta sivustoilta.",
  },

  "/sv/arbetsliv/jobb-i-finland/teknologi-loeneguide-finland-2026-04": {
    title: "Teknikloneeguide Finland april 2026 | Uppdaterade lonebenchmarks | Jobcrawls",
    description:
      "Uppdaterade lonebenchmarks for tekniska roller i Finland, april 2026. Mjukvara, data, moln och sakerhet tackt med live-data fran alla stora finska jobbsajter.",
  },

  "/en/resources/jobs-in-finland/finland-hidden-job-market-networking-2026-03": {
    title: "Finland's Hidden Job Market | Networking and Unlisted Roles | Jobcrawls",
    description:
      "Up to half of Finnish jobs are never publicly listed. Here is how to tap into the hidden job market through networking, while using Jobcrawls to cover every role that is publicly posted.",
  },

  "/fi/tyoelama/tyot-suomessa/suomen-piilotyomarkkinat-verkostoituminen-2026-03": {
    title: "Suomen piilotyomarkkinat | Verkostoituminen ja julkaisemattomat paikat | Jobcrawls",
    description:
      "Jopa puolet suomalaisista tyopaikoista ei koskaan julkaista avoimesti. Nain tavoitat piilotyomarkkinat verkostoitumalla samalla kun Jobcrawls kattaa kaikki julkisesti ilmoitetut paikat.",
  },

  "/sv/arbetsliv/jobb-i-finland/finlands-dolda-arbetsmarknad-2026-03": {
    title: "Finlands dolda arbetsmarknad | Nataverkande och oannonserade jobb | Jobcrawls",
    description:
      "Upp till halften av finska jobb annonseras aldrig offentligt. Har ar hur du kommer at den dolda arbetsmarknaden via natverkande medan Jobcrawls tackar alla offentligt annonserade roller.",
  },

  "/en/resources/summer-jobs": {
    title: "Summer Jobs in Finland | All Job Sites in One Search | Jobcrawls",
    description:
      "Find summer jobs in Finland from every Finnish job board in one search. Student roles, seasonal work and part-time positions across Helsinki, Tampere, Turku and more, updated daily.",
  },

  "/fi/tyoelama/kesatyot": {
    title: "Kesatyot Suomessa | Kaikki kesatyoilmoitukset yhdessa | Jobcrawls",
    description:
      "Loyda kesatyopaikat Suomesta kaikilta tyonhakusivustoilta yhdella haulla. Opiskelijapaikat, kausityo ja osa-aikaiset tehtavat Helsingissa, Tampereella, Turussa ja muualla. Paivitetty paivittain.",
  },

  "/sv/arbetsliv/sommarjobb-i-finland": {
    title: "Sommarjobb i Finland | Alla jobbsajter pa ett stalle | Jobcrawls",
    description:
      "Hitta sommarjobb i Finland fran alla finska jobbsajter i en sokning. Studentjobb, sasongsjobb och deltidstjanster i Helsingfors, Tammerfors, Abo och mer, uppdaterade dagligen.",
  },

  "/en/resources/jobs-in-finland/cool-finnish-tech-companies-english": {
    title: "Finnish Tech Companies That Work in English | Where to Apply | Jobcrawls",
    description:
      "The best Finnish tech companies hiring without requiring Finnish language skills. Includes company profiles, what they build and links to their open roles aggregated across all job boards.",
  },

  "/fi/tyoelama/suomalaiset-tech-yritykset-tyokielena-englanti": {
    title: "Suomalaiset tech-yritykset tyokielena englanti | Missa hakea | Jobcrawls",
    description:
      "Parhaat suomalaiset teknologiayritykset, jotka rekrytoivat ilman suomen kielen vaatimusta. Yritysprofiilit, mita ne rakentavat ja linkit avoimiin paikkoihin kaikilta sivustoilta.",
  },

  "/sv/arbetsliv/finska-techbolag-engelska-arbetssprak": {
    title: "Finska techbolag med engelska som arbetssprak | Var du soker | Jobcrawls",
    description:
      "De basta finska techbolagen som rekryterar utan krav pa finska sprakkunskaper. Foretagsprofiler, vad de bygger och lankar till oppna roller aggregerade fran alla jobbsajter.",
  },

  "/en/resources/jobs-in-finland/highest-salaries-finland": {
    title: "Highest Paying Jobs in Finland | By Role and Region | Jobcrawls",
    description:
      "Which jobs pay the most in Finland and where? Salary data by role and region drawn from live listings across all major Finnish job boards. Updated with real market data.",
  },

  "/fi/tyoelama/korkeimmat-palkat-suomessa-alueittain": {
    title: "Korkeimmat palkat Suomessa | Rooleittain ja alueittain | Jobcrawls",
    description:
      "Missa ammateissa ja millä alueilla maksetaan parhaiten Suomessa? Palkkatiedot rooleittain ja alueittain reaaliaikaisten tyopaikkailmoitusten pohjalta kaikilta suurilta suomalaisilta sivustoilta.",
  },

  "/sv/arbetsliv/hogsta-lonerna-finland-regioner": {
    title: "Hogst betalda jobben i Finland | Per roll och region | Jobcrawls",
    description:
      "Vilka jobb betalar bast i Finland och var? Lonedata per roll och region hamtad fran live-annonser pa alla stora finska jobbsajter. Uppdateras med verklig marknadsdata.",
  },

  "/en/resources/jobs-in-finland/best-job-websites-finland": {
    title: "Best Job Websites in Finland | Compared and Ranked | Jobcrawls",
    description:
      "A complete comparison of Finnish job boards: Duunitori, Jobly, Tyomarkkinatori, LinkedIn, Indeed and more. Which sites list the most roles and which are best for your industry? Or just use Jobcrawls.",
  },

  "/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa": {
    title: "Parhaat tyopaikkasivustot Suomessa | Vertailu ja ranking | Jobcrawls",
    description:
      "Kattava vertailu suomalaisista tyonhakusivustoista: Duunitori, Jobly, Tyomarkkinatori, LinkedIn, Indeed ja muut. Milla sivustoilla on eniten ilmoituksia ja mika sopii omalle alallesi? Tai kayta vain Jobcrawlsia.",
  },

  "/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland": {
    title: "Basta jobbsajter i Finland | Jamforda och rankade | Jobcrawls",
    description:
      "En komplett jamforelse av finska jobbsajter: Duunitori, Jobly, Tyomarkkinatori, LinkedIn, Indeed och fler. Vilka sajter har flest annonser och vilken passar bast for din bransch? Eller anvand bara Jobcrawls.",
  },
} as const satisfies Record<string, UrlStaticSeoMeta>;
